import { createContext, type ReactNode, useContext, useMemo } from "react"
import { useSidebarExpanded } from "../hooks/use-sidebar-expanded"

interface SidebarState {
	expanded: boolean
	toggle: () => void
}

const SidebarStateContext = createContext<SidebarState | null>(null)

/**
 * Shares the desktop sidebar's expanded/collapsed state between the Sidebar
 * (width + labels) and the shell's <main> (left padding), backed by the
 * persisted useSidebarExpanded hook.
 */
export function SidebarStateProvider({ children }: { children: ReactNode }) {
	const { expanded, setExpanded } = useSidebarExpanded()
	const value = useMemo<SidebarState>(
		() => ({ expanded, toggle: () => setExpanded(!expanded) }),
		[expanded, setExpanded],
	)
	return (
		<SidebarStateContext.Provider value={value}>
			{children}
		</SidebarStateContext.Provider>
	)
}

export function useSidebarState(): SidebarState {
	const ctx = useContext(SidebarStateContext)
	if (!ctx) {
		throw new Error(
			"useSidebarState must be used within a SidebarStateProvider",
		)
	}
	return ctx
}
